import { useState } from "react";
import { ApiError } from "@/api/fetch";
import { submitFeedback, type ClaimFeedbackRequest } from "../api/claimHunter";
import { LoginPrompt } from "./LoginPrompt";

const CSS = `
.ch-fb { margin-top: 36px; font-size: 13px; font-style: italic; color: var(--ch-ink2); }
.ch-fb summary { cursor: pointer; list-style: none; display: inline; text-decoration: underline; text-decoration-color: var(--ch-rule2); text-underline-offset: 3px; }
.ch-fb summary::-webkit-details-marker { display: none; }
.ch-fb summary:hover, .ch-fb[open] summary { color: var(--ch-ink); text-decoration-color: var(--ch-ink2); }
.ch-fb-body { margin-top: 10px; padding-left: 12px; border-left: 2px solid var(--ch-rule); max-width: 560px; }
.ch-fb-body p { margin: 0; }
.ch-fb-note { display: block; width: 100%; margin-top: 10px; border: none; border-bottom: 1px solid var(--ch-rule2); outline: none; resize: vertical; background: transparent; font: inherit; font-style: normal; font-size: 14px; color: var(--ch-ink); min-height: 40px; padding: 2px 0 6px; }
.ch-fb-note::placeholder { color: var(--ch-ink3); font-style: italic; }
.ch-fb-note:focus { border-bottom-color: var(--ch-ink); }
.ch-fb-fine { margin-top: 6px !important; font-size: 11.5px; color: var(--ch-ink3); }
.ch-fb-send { font: inherit; font-style: normal; color: var(--ch-ink); background: none; border: none; padding: 0; margin-top: 10px; cursor: pointer; text-decoration: underline; text-underline-offset: 3px; }
.ch-fb-send:disabled { color: var(--ch-ink3); cursor: wait; }
.ch-fb-done { margin-top: 10px; color: var(--ch-ink); }
.ch-fb-err { margin-top: 8px !important; color: var(--ch-against); }
`;

/** "Didn't quite get the results you're looking for?": lets the user submit the query as feedback. */
export function QueryFeedback({
  query,
  mode,
  counts,
  loggedOut = false,
}: {
  query: string;
  mode: ClaimFeedbackRequest["mode"];
  counts: ClaimFeedbackRequest["result_counts"];
  /** Feedback needs an account: submitting shows the login prompt instead. */
  loggedOut?: boolean;
}) {
  const [note, setNote] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);
  const [needLogin, setNeedLogin] = useState(false);

  const send = async () => {
    if (loggedOut) {
      setNeedLogin(true);
      return;
    }
    setState("sending");
    setError(null);
    try {
      await submitFeedback({
        query,
        mode,
        note: note.trim() || undefined,
        result_counts: counts,
      });
      setState("sent");
    } catch (err) {
      setState("idle");
      setError(err instanceof ApiError ? err.message : "Couldn't send feedback. Try again.");
    }
  };

  return (
    <details className="ch-fb">
      <style>{CSS}</style>
      <summary>Didn&rsquo;t quite get the results you&rsquo;re looking for?</summary>
      <div className="ch-fb-body">
        {state === "sent" ? (
          <p className="ch-fb-done">Thanks &mdash; we&rsquo;ve saved this query to improve results.</p>
        ) : (
          <>
            <p>We&rsquo;re still working on our model to provide the best results.</p>
            <textarea
              className="ch-fb-note"
              value={note}
              maxLength={2000}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What were you hoping to find? (optional)"
              aria-label="What were you hoping to find? (optional)"
            />
            <p className="ch-fb-fine">
              Submitting saves this query and your note so our team can review it.
            </p>
            <button
              type="button"
              className="ch-fb-send"
              disabled={state === "sending"}
              onClick={() => void send()}
            >
              {state === "sending" ? "Sending…" : "Click here to submit your query as feedback."}
            </button>
            {needLogin ? (
              <p className="ch-fb-err">
                <LoginPrompt />
              </p>
            ) : null}
            {error ? <p className="ch-fb-err">{error}</p> : null}
          </>
        )}
      </div>
    </details>
  );
}
